// src/services/ringcentral.js
// RingCentral integration: auth, SMS, click-to-dial, ACE summary retrieval

const RC_BASE = 'https://platform.ringcentral.com';

//  Auth 
// JWT flow: exchange Client ID + Client Secret + JWT token for a short-lived
// access token. We cache it in memory and refresh when it expires.

let _accessToken = null;
let _tokenExpiry = 0;

export async function getRCToken() {
  if (_accessToken && Date.now() < _tokenExpiry - 60000) {
    return _accessToken;
  }
  const creds = btoa(
    process.env.REACT_APP_RC_CLIENT_ID + ':' +
    process.env.REACT_APP_RC_CLIENT_SECRET
  );
  const res = await fetch(`${RC_BASE}/restapi/oauth/token`, {
    method: 'POST',
    headers: {
      'Authorization': `Basic ${creds}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
      assertion: process.env.REACT_APP_RC_JWT_TOKEN,
    }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(`RC auth failed: ${err.error_description || res.status}`);
  }
  const data = await res.json();
  _accessToken = data.access_token;
  _tokenExpiry = Date.now() + data.expires_in * 1000;
  return _accessToken;
}

async function rcFetch(path, options = {}) {
  const token = await getRCToken();
  const res = await fetch(`${RC_BASE}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`RC API error ${res.status}: ${text}`);
  }
  return res.json();
}

//  SMS 

// Send an outbound SMS from the Sleep Metrics main number to a patient.
// fromNumber: your RC phone number in E.164 format e.g. "+15038873531"
// toNumber:   patient phone in E.164 format e.g. "+15031234567"
// meta: { patientId (required for the safety gate), coach, templateId, automated }
// Returns a structured result. A gate block is NOT an error — the caller inspects
// `blocked` / `reasonCode`. Only real transport failures throw.
export async function sendSMS(toNumber, text, meta = {}) {
  const res = await fetch('/api/rc/send-sms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to: toNumber, message: text, ...meta }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 409 && data.blocked) return { ok: false, blocked: true, reasonCode: data.reasonCode };
  if (!res.ok) throw new Error(data.error || `Send failed (${res.status})`);
  return { ok: true, ...data };
}

// Fetch a patient's SMS thread from outreach_events (authenticated).
export async function getPatientThread(patientId, token) {
  const res = await fetch(`/api/comms/${patientId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Could not load thread (${res.status})`);
  const data = await res.json();
  return data.thread || [];
}

// Fetch recent SMS conversation between Sleep Metrics and a patient number.
// Returns messages sorted newest-first.
export async function getSMSThread(patientPhone) {
  const data = await rcFetch(
    `/restapi/v1.0/account/~/extension/~/message-store?messageType=SMS&perPage=50`
  );
  const msgs = (data.records || []).filter(m =>
    m.from?.phoneNumber === patientPhone ||
    m.to?.some(t => t.phoneNumber === patientPhone)
  );
  return msgs.map(m => ({
    id: m.id,
    direction: m.direction,
    body: m.subject,
    timestamp: m.creationTime,
    status: m.messageStatus,
  }));
}

//  Click-to-dial 

// Initiate an outbound call from the coach's RC extension to a patient.
// coachExtensionId: the RC extension ID for the coach (or "~" for main account)
// fromNumber: coach RC number in E.164
// toNumber:   patient number in E.164
// Returns a telephony session ID that can be used to track call status.
export async function initiateCall(fromNumber, toNumber) {
  // Calls server proxy - RC credentials live server-side only
  const res = await fetch('/api/rc/call', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to: toNumber }),
  });
  if (!res.ok) { const e = await res.json(); throw new Error(e.error || res.status); }
  return res.json();
}

//  ACE / RingSense summaries 

// Fetch the AI call summary for a completed call session.
// sessionId: returned from initiateCall or received via webhook
export async function getACESummary(sessionId) {
  try {
    const data = await rcFetch(
      `/ai/ringsense/v1/private/accounts/~/calls/${sessionId}/insights`
    );
    return {
      summary: data.brief || '',
      transcript: data.transcript || '',
      outcome: data.callDisposition || 'unknown',
      duration: data.duration || 0,
      sessionId,
    };
  } catch (e) {
    // Summary may not be ready yet immediately after call ends
    console.warn('ACE summary not ready:', e.message);
    return null;
  }
}

//  Webhook helpers 

// Validate that an inbound webhook POST is genuinely from RingCentral.
// validationToken header: RC sends this on subscription verification
// Returns the token if this is a verification handshake, null otherwise.
export function getWebhookValidationToken(headers) {
  return headers['validation-token'] || headers['Validation-Token'] || null;
}